import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PortalLayout } from "@/components/portal-layout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/student/attendance")({ component: MyAttendance });

const COLOR: Record<string, string> = {
  present: "var(--portal)", absent: "#ef4444", excused: "#fbbf24", late: "#fb923c",
};
const LABEL: Record<string, string> = { present: "Có mặt", absent: "Vắng KP", excused: "Có phép", late: "Muộn" };

function MyAttendance() {
  const { user } = useAuth();
  const [filterClass, setFilterClass] = React.useState("all");

  const q = useQuery({
    queryKey: ["my-attendance", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendances")
        .select("id, status, session_id, marked_at, class_sessions(starts_at, class_id)")
        .eq("student_id", user!.id).order("marked_at", { ascending: false }).limit(60);
      if (error) throw error;
      const rows = data ?? [];
      const classIds = [...new Set(rows
        .map(r => (r.class_sessions as { class_id?: string } | null)?.class_id)
        .filter((x): x is string => !!x))];
      const { data: classes } = classIds.length
        ? await supabase.from("classes").select("id, name").in("id", classIds)
        : { data: [] };
      const classMap = new Map((classes ?? []).map(c => [c.id, c.name]));
      return rows.map(r => {
        const sess = r.class_sessions as { starts_at: string; class_id: string } | null;
        return {
          ...r,
          class_id: sess?.class_id ?? null,
          className: sess?.class_id ? classMap.get(sess.class_id) ?? "Lớp" : "Lớp",
        };
      });
    },
  });

  const allRows = q.data ?? [];

  const classOptions = React.useMemo(() => {
    const map = new Map<string, string>();
    allRows.forEach(r => { if (r.class_id) map.set(r.class_id, r.className); });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [allRows]);

  const rows = React.useMemo(() => {
    if (filterClass === "all") return allRows;
    return allRows.filter(r => r.class_id === filterClass);
  }, [allRows, filterClass]);

  const present = rows.filter(r => r.status === "present").length;
  const absent = rows.filter(r => r.status === "absent").length;
  const excused = rows.filter(r => r.status === "excused").length;
  const late = rows.filter(r => r.status === "late").length;
  const total = rows.length;
  const rate = total ? Math.round((present / total) * 100) : 0;

  return (
    <PortalLayout portal="student" title="Điểm danh của tôi" breadcrumb={[{ label: "Học sinh", to: "/student" }, { label: "Điểm danh" }]}>
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả lớp</SelectItem>
            {classOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Tổng buổi", v: total, c: "bg-card" },
          { label: "Có mặt", v: present, c: "bg-emerald-50" },
          { label: "Có phép", v: excused, c: "bg-amber-50" },
          { label: "Vắng KP", v: absent, c: "bg-rose-50" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className={`border rounded-2xl p-5 ${s.c}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{s.label}</div>
            <div className="font-display text-3xl font-semibold">{s.v}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bg-card border rounded-2xl p-6 shadow-sm grid place-items-center text-center">
          <div className="relative size-44">
            <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="oklch(0.95 0.005 247)" strokeWidth="10" />
              <motion.circle cx="50" cy="50" r="42" fill="none" stroke="var(--portal)" strokeWidth="10" strokeLinecap="round"
                strokeDasharray={264} initial={{ strokeDashoffset: 264 }} animate={{ strokeDashoffset: 264 - (264 * rate) / 100 }} transition={{ duration: 1.2 }} />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <div>
                <div className="font-display text-4xl font-semibold" style={{ color: "var(--portal)" }}>{rate}%</div>
                <div className="text-xs text-muted-foreground mt-1">Tỉ lệ có mặt</div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-xs text-muted-foreground">Muộn: {late}</div>
        </div>

        <div className="lg:col-span-2 bg-card border rounded-2xl p-6 shadow-sm">
          <h3 className="font-display font-semibold mb-4">Lịch sử điểm danh</h3>
          {rows.length === 0 ? <div className="text-sm text-muted-foreground">Chưa có dữ liệu.</div> : (
            <ul className="divide-y">
              {rows.map(r => {
                const s = r.class_sessions as { starts_at: string } | null;
                return (
                  <li key={r.id} className="py-2.5 flex items-center gap-3">
                    <div className="size-2.5 rounded-full" style={{ background: COLOR[r.status] }} />
                    <div className="flex-1 text-sm">
                      <div>{s ? format(new Date(s.starts_at), "EEE dd/MM/yyyy HH:mm", { locale: vi }) : "—"}</div>
                      <div className="text-xs text-muted-foreground">{r.className}</div>
                    </div>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: COLOR[r.status] + "22", color: COLOR[r.status] }}>{LABEL[r.status]}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
